import React from 'react';

/**
 * ErrorBoundary — Catches React render errors and displays a recovery UI.
 * 
 * Primary use case: prevents blank white screen when:
 * - Lazy-loaded chunks fail (ChunkLoadError after a new deploy)
 * - Components throw during render
 * - Network issues cause import() failures
 * 
 * Wraps the entire App to guarantee the user always sees SOMETHING.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReload = () => {
    // Save current path so user returns here after reload
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath && currentPath !== '/' && currentPath !== '/login') {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
    }
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/app';
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error?.name === 'ChunkLoadError'
        || this.state.error?.message?.includes('Loading chunk')
        || this.state.error?.message?.includes('Failed to fetch dynamically imported module')
        || this.state.error?.message?.includes('Importing a module script failed');

      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--archon-bg-app)',
          color: 'var(--archon-text)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          <div style={{ textAlign: 'center', maxWidth: 480, padding: 32 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {isChunkError ? '🔄' : '⚠️'}
            </div>
            <h2 style={{ color: 'var(--archon-text)', marginBottom: 8 }}>
              {isChunkError ? 'New Version Available' : 'Something went wrong'}
            </h2>
            <p style={{ color: 'var(--archon-text-muted)', marginBottom: 24, lineHeight: 1.6 }}>
              {isChunkError
                ? 'A new version of the application has been deployed. Please reload to get the latest update.'
                : 'An unexpected error occurred. You can try reloading the page or going back to the home screen.'}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '10px 24px',
                  backgroundColor: 'var(--archon-primary)',
                  color: 'var(--archon-on-header)',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '10px 24px',
                  backgroundColor: 'transparent',
                  color: 'var(--archon-text-muted)',
                  border: '1px solid var(--archon-border)',
                  borderRadius: 6,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
